import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import {
    ActionBar,
    BottomSheet,
    BottomSheetFooter,
    Button,
    Modal,
    NavigationBar,
    SearchInput,
    useBreakpoint,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { makeSetStoreField } from '@hh.ru/redux-create-reducer/lib/createReducer';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import debounce from 'bloko/common/debounce';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Form from 'src/components/Form';
import { useNotification } from 'src/components/Notifications/Provider';
import { fetchBannerClose } from 'src/components/Search/Resume/ContextVacancy/api/bannerClose';
import { searchActiveVacancies } from 'src/components/Search/Resume/ContextVacancy/api/searchActiveVacancies';
import {
    AnalyticsParams,
    ResumeSearchContextAction,
    ResumeSearchContextSource,
    sendEventAction,
    sendEventElementShown,
} from 'src/components/Search/Resume/ContextVacancy/utils/analytics';
import { getUrlWithVacancyFilters } from 'src/components/Search/Resume/ContextVacancy/utils/getUrlWithVacancyFilters';
import { getUrlWithoutVacancy } from 'src/components/Search/Resume/ContextVacancy/utils/getUrlWithoutVacancy';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { ContextVacancy, setResumeSearchContextActiveVacancies } from 'src/models/resumeSearchContext';

import ContextVacancyOption from 'src/components/Search/Resume/ContextVacancy/VacancySelector/VacacncyOption';

const setContextVacancySelectorVisible = makeSetStoreField('resumeSearchContextVacancySelectorVisible');

const FILTER_DEBOUNCE = 300;
const SEARCH_QUERY_LIMIT_LOW = 3;

export const CONTEXT_VACANCY_ID_NOT_SELECTED = -1;

const TrlKeys = {
    title: 'resume.search.context.vacancySelector.title',
    description: {
        mobile: 'resume.search.context.vacancySelector.description',
        desktop: 'resume.search.context.vacancySelector.description.desktop',
    },
    searchPlaceholder: 'resume.search.context.vacancySelector.searchPlaceholder',
    notSelectedOption: 'resume.search.context.vacancySelector.notSelectedOption',
    action: {
        close: 'resume.search.context.vacancySelector.action.close',
        apply: 'resume.search.context.vacancySelector.action.apply',
    },
};

const ContextVacancySelector: TranslatedComponent = ({ trls }) => {
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const isVisible = useSelector((state) => state.resumeSearchContextVacancySelectorVisible);
    const vacanciesSelector = useSelector((state) => state.resumeSearchContext?.managerActiveVacancies);
    const vacancies = useMemo(() => vacanciesSelector || [], [vacanciesSelector]);

    const vacancyNotSelectedOption: ContextVacancy = useMemo(
        () => ({
            id: CONTEXT_VACANCY_ID_NOT_SELECTED,
            name: trls[TrlKeys.notSelectedOption],
            filters: { trls: [], query: {} },
        }),
        [trls]
    );
    const currentVacancy = useSelector((state) => state.resumeSearchContext?.vacancy) || vacancyNotSelectedOption;

    // local state
    const [vacanciesToRender, setVacanciesToRender] = useState(vacancies);
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedVacancy, setSelectedVacancy] = useState<ContextVacancy>(currentVacancy);

    useEffect(() => {
        setSelectedVacancy(currentVacancy);
    }, [currentVacancy]);

    // search
    const { addNotification } = useNotification();
    const debouncedSearch = useMemo(
        () =>
            debounce(async (query: string) => {
                const data = await searchActiveVacancies(query, addNotification);
                if (query === '' && data.length !== vacancies.length) {
                    dispatch(setResumeSearchContextActiveVacancies(data));
                } else {
                    setVacanciesToRender(data);
                }
            }, FILTER_DEBOUNCE),
        [addNotification, dispatch, vacancies.length]
    );
    const handleSearchQueryChange = useCallback(
        (query: string) => {
            setSearchQuery(query);
            if (query.length >= SEARCH_QUERY_LIMIT_LOW || query === '') {
                debouncedSearch(query);
            } else {
                setVacanciesToRender(vacancies);
            }
        },
        [debouncedSearch, vacancies]
    );

    // analytics
    const employerId = useSelector((state) => state.employerId);
    const employerManagerId = useSelector((state) => state.employerManager?.id) || '';
    const baseAnalyticsParams: AnalyticsParams = useMemo(
        () => ({
            vacancyId: currentVacancy.id,
            filters: JSON.stringify(currentVacancy.filters.query),
            employerId,
            employerManagerId,
            hhtmSourceLabel: ResumeSearchContextSource.VacancySelector,
        }),
        [currentVacancy.filters.query, currentVacancy.id, employerId, employerManagerId]
    );

    // close logic
    const close = useCallback(
        (isSubmit?: boolean) => {
            dispatch(setContextVacancySelectorVisible(false));
            if (searchQuery.length > 0) {
                setSearchQuery('');
                setVacanciesToRender(vacancies);
            }
            if (!isSubmit && currentVacancy.id !== selectedVacancy.id) {
                setSelectedVacancy(currentVacancy);
            }
        },
        [currentVacancy, dispatch, searchQuery.length, selectedVacancy.id, vacancies]
    );

    // close button logic
    const handleClose = useCallback(() => {
        close();
        sendEventAction(ResumeSearchContextAction.Close, baseAnalyticsParams);
    }, [baseAnalyticsParams, close]);

    // apply button logic
    const urlSearchParams = useSelectorNonNullable((state) => state.router.location.search);
    const getUpdatedUrl = useCallback(() => {
        if (selectedVacancy.id === CONTEXT_VACANCY_ID_NOT_SELECTED) {
            return getUrlWithoutVacancy(urlSearchParams);
        }
        return getUrlWithVacancyFilters(urlSearchParams, selectedVacancy.filters.query);
    }, [selectedVacancy.filters.query, selectedVacancy.id, urlSearchParams]);

    const handleSubmit = useCallback(() => {
        close(true);
        if (selectedVacancy.id === CONTEXT_VACANCY_ID_NOT_SELECTED) {
            void fetchBannerClose(true);
            sendEventAction(ResumeSearchContextAction.Close, baseAnalyticsParams);
            return;
        }
        sendEventAction(ResumeSearchContextAction.Apply, {
            ...baseAnalyticsParams,
            selectedVacancyId: selectedVacancy.id,
            selectedFilters: JSON.stringify(selectedVacancy.filters.query || {}),
        });
    }, [baseAnalyticsParams, close, selectedVacancy]);

    // render components
    const searchInput = (
        <SearchInput
            value={searchQuery}
            onChange={handleSearchQueryChange}
            placeholder={trls[TrlKeys.searchPlaceholder]}
            clearable
            data-qa="resume-search-context-vacancy-selector-search-input"
        />
    );

    const actions = (
        <>
            <Button
                stretched={isMobile}
                mode="secondary"
                style="accent"
                onClick={handleClose}
                data-qa="resume-search-context-action-close"
            >
                {trls[TrlKeys.action.close]}
            </Button>
            <Button
                stretched={isMobile}
                mode="primary"
                style="accent"
                Element={SPALink}
                to={getUpdatedUrl()}
                onClick={handleSubmit}
                data-qa="resume-search-context-action-apply"
            >
                {trls[TrlKeys.action.apply]}
            </Button>
        </>
    );

    const content = (
        <ElementShownAnchor fn={sendEventElementShown} {...baseAnalyticsParams}>
            <Form data-qa="resume-search-context-vacancy-selector">
                <VSpacingContainer default={12}>
                    {([vacancyNotSelectedOption, ...vacanciesToRender] as Array<ContextVacancy>).map((vacancy) => (
                        <ContextVacancyOption
                            key={vacancy.id}
                            vacancy={vacancy}
                            selectedVacancyId={selectedVacancy.id}
                            setSelectedVacancy={setSelectedVacancy}
                        />
                    ))}
                </VSpacingContainer>
            </Form>
        </ElementShownAnchor>
    );

    return isMobile ? (
        <BottomSheet
            visible={isVisible}
            height="full-screen"
            header={
                <NavigationBar
                    title={trls[TrlKeys.title]}
                    subtitle={trls[TrlKeys.description.mobile]}
                    options={searchInput}
                    showDivider="with-scroll"
                />
            }
            footer={<BottomSheetFooter>{actions}</BottomSheetFooter>}
            onClose={handleClose}
        >
            {content}
        </BottomSheet>
    ) : (
        <Modal
            visible={isVisible}
            title={trls[TrlKeys.title]}
            titleDescription={trls[TrlKeys.description.desktop]}
            options={searchInput}
            footer={<ActionBar primaryActions={actions} />}
            onClose={handleClose}
        >
            {content}
        </Modal>
    );
};

export default translation(ContextVacancySelector);
